import { CopyIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { CalculateIcon } from '@icons/CalculateIcon'
import { useMemo, useState } from 'react'

type CalcMode = 'lmp' | 'conception' | 'both'

const addDays = (date: Date, days: number): Date => {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

const formatEdd = (d: Date): string => {
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${mm}/${dd}/${yyyy}`
}

const calcGa = (edd: Date): { weeks: number; days: number } | null => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const eddMidnight = new Date(edd)
  eddMidnight.setHours(0, 0, 0, 0)
  const totalDays = Math.round(
    (today.getTime() - eddMidnight.getTime()) / (1000 * 60 * 60 * 24) + 280,
  )
  if (totalDays < 0 || totalDays > 315) return null
  return { weeks: Math.floor(totalDays / 7), days: totalDays % 7 }
}

const GaDisplay = ({ edd }: { edd: Date }) => {
  const ga = calcGa(edd)
  if (!ga)
    return (
      <Text color="gray.500" fontSize="sm">
        GA not in range
      </Text>
    )
  return (
    <Text fontSize="sm" color="gray.700" _dark={{ color: 'gray.200' }}>
      GA:{' '}
      <strong>
        {ga.weeks}w {ga.days}d
      </strong>
    </Text>
  )
}

const EddRow = ({
  label,
  edd,
  onCopy,
}: {
  label: string
  edd: Date
  onCopy: (text: string) => void
}) => (
  <Flex align="center" gap={2} wrap="wrap">
    <Text fontSize="sm" color="gray.500" minW="120px">
      {label}
    </Text>
    <Badge colorScheme="teal" fontSize="sm" px={2} py={0.5}>
      {formatEdd(edd)}
    </Badge>
    <GaDisplay edd={edd} />
    <Tooltip label="Copy EDD" hasArrow>
      <IconButton
        size="xs"
        variant="ghost"
        aria-label="Copy EDD"
        icon={<CopyIcon />}
        onClick={() => onCopy(formatEdd(edd))}
      />
    </Tooltip>
  </Flex>
)

export const PregnancyCalculatorPanel = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const [mode, setMode] = useState<CalcMode>('lmp')
  const [lmpDate, setLmpDate] = useState('')
  const [conceptionDate, setConceptionDate] = useState('')
  const toast = useToast()

  const lmpEdd = useMemo(() => {
    if (!lmpDate) return null
    const d = new Date(lmpDate + 'T12:00:00')
    if (isNaN(d.getTime())) return null
    return addDays(d, 280)
  }, [lmpDate])

  const conceptionEdd = useMemo(() => {
    if (!conceptionDate) return null
    const d = new Date(conceptionDate + 'T12:00:00')
    if (isNaN(d.getTime())) return null
    return addDays(d, 266)
  }, [conceptionDate])

  const diffDays = useMemo(() => {
    if (!lmpEdd || !conceptionEdd) return null
    return Math.abs(
      Math.round((lmpEdd.getTime() - conceptionEdd.getTime()) / (1000 * 60 * 60 * 24)),
    )
  }, [lmpEdd, conceptionEdd])

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() =>
        toast({ title: 'EDD copied', status: 'success', duration: 1500, isClosable: true }),
      )
      .catch(() => toast({ title: 'Copy failed', status: 'error', duration: 1500 }))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          borderBottomWidth={1}
          py={3}
          bg="gray.50"
          borderTopRadius="md"
          fontFamily="Comfortaa"
          fontSize="md">
          Pregnancy Calculator
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text fontSize="sm" color="gray.500" mb={2}>
            Quick reference only — no data saved.
          </Text>

          <RadioGroup
            colorScheme="green"
            value={mode}
            onChange={v => setMode(v as CalcMode)}
            mb={2}>
            <Stack direction="row" spacing={4} wrap="wrap">
              <Radio value="lmp" size="sm">
                LMP only
              </Radio>
              <Radio value="conception" size="sm">
                Conception only
              </Radio>
              <Radio value="both" size="sm">
                Both
              </Radio>
            </Stack>
          </RadioGroup>

          <VStack align="stretch" spacing={4}>
            {(mode === 'lmp' || mode === 'both') && (
              <Box>
                <Text fontSize="xs" fontWeight={600} color="gray.600" mb={1}>
                  Last Menstrual Period (LMP)
                </Text>
                <Input
                  type="date"
                  size="sm"
                  value={lmpDate}
                  onChange={e => setLmpDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                />
                {lmpEdd && (
                  <Box mt={2}>
                    <EddRow label="EDD (LMP + 280d):" edd={lmpEdd} onCopy={copyToClipboard} />
                  </Box>
                )}
              </Box>
            )}

            {(mode === 'conception' || mode === 'both') && (
              <Box>
                <Text fontSize="xs" fontWeight={600} color="gray.600" mb={1}>
                  Conception Date
                </Text>
                <Input
                  type="date"
                  size="sm"
                  value={conceptionDate}
                  onChange={e => setConceptionDate(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                />
                {conceptionEdd && (
                  <Box mt={2}>
                    <EddRow
                      label="EDD (conception + 266d):"
                      edd={conceptionEdd}
                      onCopy={copyToClipboard}
                    />
                  </Box>
                )}
              </Box>
            )}

            {mode === 'both' && lmpEdd && conceptionEdd && (
              <>
                <Divider />
                <HStack>
                  <Text fontSize="sm" color="gray.600">
                    Difference:
                  </Text>
                  <Text fontSize="sm" fontWeight={600}>
                    {diffDays} {diffDays === 1 ? 'day' : 'days'}
                  </Text>
                </HStack>
                <Button
                  size="xs"
                  variant="outline"
                  leftIcon={<CopyIcon />}
                  onClick={() =>
                    copyToClipboard(
                      `LMP EDD: ${formatEdd(lmpEdd)} | Conception EDD: ${formatEdd(conceptionEdd)} | Difference: ${diffDays}d`,
                    )
                  }>
                  Copy both EDDs
                </Button>
              </>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export const PregnancyCalcButton = () => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <>
      <Tooltip label="Pregnancy Calculator" hasArrow>
        <IconButton
          variant="ghost"
          size="sm"
          fontWeight={400}
          icon={<CalculateIcon />}
          fontFamily="hero-new"
          onClick={() => setIsOpen(true)}
          aria-label="Pregnancy Calculator"
        />
      </Tooltip>
      <PregnancyCalculatorPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  )
}
